import BaseModal from "../../../components/ui/BaseModal";
import BaseButton from "../../../components/ui/BaseButton";
import QualificationForm from "../../WorkOrder/Forms/QualificationForm";
import { useSelector, useDispatch } from "react-redux";
import { showToast } from "../../../store/toast/toastSlice";
import { useMutation } from "@tanstack/react-query";
import { useUpdateWorkOrder } from "../../../hooks/useUpdateWorkOrder";
import { updateWorkOrderApi } from "../../../services/api/workOrder";
import { useRef } from "react";
import { useStoreSectionChanges } from "../../../hooks/useSectionChanges";
import {
  buildValidationMessage,
  focusFirstInvalidField,
  normalizeValidationResult,
} from "../../../utils/formErrors";
import { useResetValidationOnClose } from "../../../hooks/useResetValidationOnClose";

const VALIDATED_FIELDS = ["qualificationTypeId", "qualificationSubTypes"];

const selectQualifications = (state) => state.workOrder.scheduling.qualifications;

const normalizeQualifications = (qualifications) =>
  (qualifications ?? [])
    .filter((qualification) => qualification?.qualificationTypeId)
    .map((qualification) => ({
      qualificationTypeId: qualification.qualificationTypeId,
      qualificationSubTypes: [...(qualification.qualificationSubTypes ?? [])].sort((a, b) =>
        String(a).localeCompare(String(b)),
      ),
    }));
const hasSaveableQualifications = (qualifications) => {
  const typed = (qualifications ?? []).filter((q) => q?.qualificationTypeId);

  return typed.length > 0 && typed.every((q) => (q?.qualificationSubTypes?.length ?? 0) > 0);
};

const EditQualificationModal = ({ open, close }) => {
  const dispatch = useDispatch();
  const id = useSelector((state) => state.workOrder.workOrderDetails.id);
  const { updateWorkOrder } = useUpdateWorkOrder();
  const workOrderEligibilityRef = useRef();
  const qualifications = useSelector((state) => state.workOrder.scheduling.qualifications);
  // QualificationForm reads the qualifications straight off the store rather than
  // hydrating them, so the values the modal opened with are the baseline.
  const hasChanges = useStoreSectionChanges(
    selectQualifications,
    qualifications,
    normalizeQualifications,
  );
  const canSave = hasSaveableQualifications(qualifications);
  useResetValidationOnClose(VALIDATED_FIELDS);

  const { isPending: isUpdatingLoading, mutateAsync: updateWorkOrderApiMutation } = useMutation({
    mutationFn: updateWorkOrderApi,
    onSuccess: () => {},
  });
  const formatWorkOrderQualifications = (qualifications) => {
    const allSubTypes = qualifications
      .map((qualification) => qualification.qualificationSubTypes)
      .flat();

    return allSubTypes;
  };
  const handleSubmit = async () => {
    const { valid, errors } = normalizeValidationResult(
      workOrderEligibilityRef.current?.isValid?.(),
    );

    if (!valid) {
      const message = buildValidationMessage(errors);
      if (message) {
        dispatch(showToast({ message, type: "error" }));
      }
      focusFirstInvalidField();
      return;
    }

    try {
      const allQualificationSubTypes = formatWorkOrderQualifications(qualifications);

      await updateWorkOrderApiMutation({
        id,
        qualificationSubTypes: allQualificationSubTypes,
      });

      dispatch(
        showToast({
          message: "Qualifications updated successfully!",
          type: "success",
        }),
      );
      updateWorkOrder();

      close();
    } catch (error) {
      console.error(error);
      dispatch(
        showToast({
          message: "Failed to update qualifications.",
          type: "error",
        }),
      );
    }
  };

  return (
    <BaseModal className="sm:-top-[200px] overflow-auto" open={open} close={close}>
      <div className="py-3 sm:py-7 px-4 sm:px-12 w-full sm:w-[800px] lg:w-[1000px] xl:w-[1400px]">
        <h6 className="text-dark font-normal border-b pb-1">Edit Qualifications</h6>

        <div className="mb-2 mt-3">
          <QualificationForm ref={workOrderEligibilityRef} isEditMode={true} />
        </div>
      </div>

      <div
        className="d-flex content-center bg-white py-6"
        style={{
          boxShadow: "0px 0px 14px 0px rgba(0, 0, 0, 0.07)",
        }}
      >
        <BaseButton
          className="px-[28px] py-[5px] bg-light text-primary hover:!text-primary"
          variant="outlined"
          color="primary"
          style={{ marginRight: "16px" }}
          onClick={() => close(false)}
        >
          Cancel
        </BaseButton>

        <BaseButton
          className="px-[12px] py-[5px] min-w-32"
          color="primary"
          onClick={handleSubmit}
          disabled={!hasChanges || !canSave || isUpdatingLoading}
        >
          Save Changes
        </BaseButton>
      </div>
    </BaseModal>
  );
};

export default EditQualificationModal;
